import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, FileDown, Send, CheckCircle2, XCircle, Building2, Package, ListChecks, ChevronRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import DashboardLayout from '../../components/layout/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => `R ${(Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  draft:     'bg-slate-700 text-slate-200',
  sent:      'bg-blue-600 text-white',
  received:  'bg-emerald-600 text-white',
  cancelled: 'bg-rose-700 text-white',
};

/**
 * Procurement content (suppliers + POs). Renders without DashboardLayout so it
 * can be embedded inside another tabbed page (e.g. Auto Spares Sales dashboard).
 */
export function ProcurementContent() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [currentUser, setCurrentUser] = useState({});
  const isAdmin = ['admin', 'auto_spares_admin', 'auto_spares_super'].includes(currentUser.role);

  useEffect(() => {
    axios.get(`${API_URL}/api/auth/me`, { headers })
      .then(res => setCurrentUser(res.data || {}))
      .catch(() => setCurrentUser({}));
  }, []); // eslint-disable-line

  const [tab, setTab] = useState('dashboard');

  // Pretty-print error.detail (which may be a dict like {code, message, unlinked})
  const getDetailMsg = (err) => {
    const d = err?.response?.data?.detail;
    if (!d) return null;
    if (typeof d === 'string') return d;
    if (d.message) return d.message;
    try { return JSON.stringify(d); } catch { return null; }
  };

  // Suppliers + products
  const [suppliers, setSuppliers] = useState([]);
  const [activeSupplier, setActiveSupplier] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [showSupplierDlg, setShowSupplierDlg] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({ company_name: '', email: '', phone: '', address: '', notes: '' });

  const [showProductDlg, setShowProductDlg] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', sku: '', price: '', notes: '' });

  // Purchase orders
  const [pos, setPos] = useState([]);
  const [showPOCreator, setShowPOCreator] = useState(false);
  const [poDraft, setPoDraft] = useState({ supplier_id: '', notes: '', line_items: [], vat_rate: 15, payment_source: 'cash' });
  const [activePO, setActivePO] = useState(null);

  // Stock catalog + expansion projects (for PO line items)
  const [stockCatalog, setStockCatalog] = useState([]);
  const [expansionProjects, setExpansionProjects] = useState([]);
  const [stockSearch, setStockSearch] = useState('');

  // VAT / SARS summary
  const [vatSummary, setVatSummary] = useState(null);

  // Procurement dashboard (KPI)
  const [dashboard, setDashboard] = useState(null);

  // Markup review (admin sets selling_price on received PO lines)
  const [markupPO, setMarkupPO] = useState(null);  // the PO being marked-up
  const [markupLines, setMarkupLines] = useState([]);
  const [markupLoading, setMarkupLoading] = useState(false);

  // ============ LOADERS ============
  const loadSuppliers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/suppliers`, { headers });
      setSuppliers(res.data || []);
    } catch { toast.error('Failed to load suppliers'); }
  };
  const loadSupplierProducts = async (supplierId) => {
    if (!supplierId) { setSupplierProducts([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/procurement/suppliers/${supplierId}/products`, { headers });
      setSupplierProducts(res.data || []);
    } catch { toast.error('Failed to load products'); }
  };
  const loadPOs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/purchase-orders`, { headers });
      setPos(res.data || []);
    } catch { toast.error('Failed to load POs'); }
  };
  const loadVatSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/vat-summary`, { headers });
      setVatSummary(res.data);
    } catch (err) { console.error('loadVatSummary failed', err); }
  };
  const loadDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) { console.error('loadDashboard failed', err); }
  };
  const changePaymentSource = async (po, source) => {
    try {
      await axios.post(`${API_URL}/api/procurement/purchase-orders/${po.id}/payment-source`, { payment_source: source }, { headers });
      toast.success(`Payment source updated to ${source.toUpperCase()}`);
      await Promise.all([loadPOs(), loadVatSummary(), loadDashboard()]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };
  const loadStockCatalog = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/stock-catalog`, { headers });
      setStockCatalog(res.data || []);
    } catch (err) { console.error('loadStockCatalog failed', err); }
  };
  const loadExpansionProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/expansion-projects-lite`, { headers });
      setExpansionProjects(res.data || []);
    } catch (err) { console.error('loadExpansionProjects failed', err); }
  };

  useEffect(() => { loadSuppliers(); loadPOs(); loadVatSummary(); loadDashboard(); loadStockCatalog(); loadExpansionProjects(); }, []); // eslint-disable-line
  useEffect(() => { if (activeSupplier) loadSupplierProducts(activeSupplier.id); }, [activeSupplier]); // eslint-disable-line

  // ============ SUPPLIER CRUD ============
  const openNewSupplier = () => {
    setEditingSupplier(null);
    setSupplierForm({ company_name: '', email: '', phone: '', address: '', notes: '' });
    setShowSupplierDlg(true);
  };
  const openEditSupplier = (s) => {
    setEditingSupplier(s);
    setSupplierForm({
      company_name: s.company_name || '',
      email: s.email || '',
      phone: s.phone || '',
      address: s.address || '',
      notes: s.notes || '',
    });
    setShowSupplierDlg(true);
  };
  const saveSupplier = async () => {
    if (!supplierForm.company_name.trim()) { toast.error('Company name required'); return; }
    try {
      if (editingSupplier) {
        await axios.put(`${API_URL}/api/procurement/suppliers/${editingSupplier.id}`, supplierForm, { headers });
        toast.success('Supplier updated');
      } else {
        await axios.post(`${API_URL}/api/procurement/suppliers`, supplierForm, { headers });
        toast.success('Supplier added');
      }
      setShowSupplierDlg(false);
      await loadSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const deleteSupplier = async (s) => {
    if (!window.confirm(`Delete ${s.company_name}? This also removes their product list (POs are kept for history).`)) return;
    try {
      await axios.delete(`${API_URL}/api/procurement/suppliers/${s.id}`, { headers });
      toast.success('Supplier deleted');
      if (activeSupplier?.id === s.id) setActiveSupplier(null);
      await loadSuppliers();
    } catch { toast.error('Delete failed'); }
  };

  // ============ PRODUCT CRUD ============
  const openNewProduct = () => {
    setEditingProduct(null);
    setProductForm({ name: '', sku: '', price: '', notes: '' });
    setShowProductDlg(true);
  };
  const openEditProduct = (p) => {
    setEditingProduct(p);
    setProductForm({ name: p.name || '', sku: p.sku || '', price: p.price ?? '', notes: p.notes || '' });
    setShowProductDlg(true);
  };
  const saveProduct = async () => {
    if (!productForm.name.trim()) { toast.error('Product name required'); return; }
    if (productForm.price === '' || isNaN(parseFloat(productForm.price))) { toast.error('Valid price required'); return; }
    const payload = {
      name: productForm.name.trim(),
      sku: productForm.sku.trim() || null,
      price: parseFloat(productForm.price),
      notes: productForm.notes.trim() || null,
    };
    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/api/procurement/suppliers/${activeSupplier.id}/products/${editingProduct.id}`, payload, { headers });
        toast.success('Product updated');
      } else {
        await axios.post(`${API_URL}/api/procurement/suppliers/${activeSupplier.id}/products`, payload, { headers });
        toast.success('Product added');
      }
      setShowProductDlg(false);
      await Promise.all([loadSupplierProducts(activeSupplier.id), loadSuppliers()]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const deleteProduct = async (p) => {
    if (!window.confirm(`Remove ${p.name}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/procurement/suppliers/${activeSupplier.id}/products/${p.id}`, { headers });
      toast.success('Product removed');
      await Promise.all([loadSupplierProducts(activeSupplier.id), loadSuppliers()]);
    } catch { toast.error('Delete failed'); }
  };

  // ============ PURCHASE ORDER ============
  const openNewPO = () => {
    setPoDraft({ supplier_id: '', notes: '', line_items: [], vat_rate: 15, payment_source: 'cash' });
    setStockSearch('');
    setShowPOCreator(true);
    // refresh lookups so latest stock/projects show up
    loadStockCatalog();
    loadExpansionProjects();
  };
  const addStockLine = (product) => {
    setPoDraft({
      ...poDraft,
      line_items: [...poDraft.line_items, {
        product_id: product.id,             // legacy/required by backend model
        stock_product_id: product.id,        // links to inventory/catalog by id
        name: product.name,
        sku: product.sku || product.supplier_sku || '',
        quantity: 1,
        unit_price: Number(product.default_cost ?? product.cost_price) || 0,
        item_type: 'stock',
        expansion_project_id: null,
      }],
    });
    setStockSearch('');
  };
  const addExpansionLine = () => {
    setPoDraft({
      ...poDraft,
      line_items: [...poDraft.line_items, {
        product_id: `exp-${Date.now()}`,
        stock_product_id: null,
        name: '',
        sku: '',
        quantity: 1,
        unit_price: 0,
        item_type: 'expansion',
        expansion_project_id: expansionProjects[0]?.id || null,
      }],
    });
  };
  // Legacy supplier-product picker path (kept for existing supplier catalog flow — defaults to stock without code link)
  const addLineItem = (product) => {
    if (poDraft.line_items.find(li => li.product_id === product.id)) {
      toast.info('Already added — adjust quantity instead');
      return;
    }
    setPoDraft({
      ...poDraft,
      line_items: [...poDraft.line_items, {
        product_id: product.id,
        stock_product_id: null,
        name: product.name,
        sku: product.sku || '',
        quantity: 1,
        unit_price: product.price,
        item_type: 'stock',
        expansion_project_id: null,
      }],
    });
  };
  const updateLineItem = (idx, patch) => {
    const next = [...poDraft.line_items];
    next[idx] = { ...next[idx], ...patch };
    setPoDraft({ ...poDraft, line_items: next });
  };
  const removeLineItem = (idx) => {
    const next = poDraft.line_items.filter((_, i) => i !== idx);
    setPoDraft({ ...poDraft, line_items: next });
  };

  const poSubtotal = useMemo(() => poDraft.line_items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0), [poDraft.line_items]);
  const poVat = useMemo(() => poSubtotal * (Number(poDraft.vat_rate) || 0) / 100, [poSubtotal, poDraft.vat_rate]);
  const poTotalIncl = poSubtotal + poVat;
  const poDraftSupplierProducts = useMemo(() => {
    if (!poDraft.supplier_id) return [];
    return supplierProducts.filter(p => true); // products are loaded for activeSupplier
  }, [poDraft.supplier_id, supplierProducts]);

  // When the user picks a supplier inside the PO creator, fetch that supplier's product list
  const onPickPOSupplier = async (supplierId) => {
    setPoDraft({ ...poDraft, supplier_id: supplierId, line_items: [] });
    if (supplierId) {
      try {
        const res = await axios.get(`${API_URL}/api/procurement/suppliers/${supplierId}/products`, { headers });
        setSupplierProducts(res.data || []);
      } catch { toast.error('Failed to load supplier products'); }
    }
  };

  const savePO = async () => {
    if (!poDraft.supplier_id) { toast.error('Pick a supplier'); return; }
    if (!poDraft.line_items.length) { toast.error('Add at least one line item'); return; }
    // Validate each line
    for (const li of poDraft.line_items) {
      if (!li.name || !li.name.trim()) { toast.error('Each line needs a name'); return; }
      if (li.item_type === 'expansion' && !li.expansion_project_id) {
        toast.error(`Pick an expansion project for "${li.name}"`); return;
      }
    }
    try {
      const payload = {
        supplier_id: poDraft.supplier_id,
        notes: poDraft.notes || null,
        vat_rate: Number(poDraft.vat_rate) || 15,
        payment_source: poDraft.payment_source || 'cash',
        line_items: poDraft.line_items.map(li => ({
          product_id: li.product_id,
          name: (li.name || '').trim(),
          sku: li.sku || null,
          quantity: parseInt(li.quantity, 10) || 1,
          unit_price: parseFloat(li.unit_price) || 0,
          item_type: li.item_type || 'stock',
          stock_product_id: li.stock_product_id || null,
          expansion_project_id: li.expansion_project_id || null,
        })),
      };
      await axios.post(`${API_URL}/api/procurement/purchase-orders`, payload, { headers });
      toast.success('Purchase Order created');
      setShowPOCreator(false);
      await Promise.all([loadPOs(), loadVatSummary(), loadDashboard()]);
      setTab('purchase-orders');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  // Receive-link dialog state
  const [linkerPO, setLinkerPO] = useState(null);
  const [linkerLines, setLinkerLines] = useState([]);
  const [linkerProjects, setLinkerProjects] = useState([]);
  const [linkerLoading, setLinkerLoading] = useState(false);

  const changeStatus = async (po, status) => {
    // Intercept "received": always open linker dialog first to confirm/link stock items
    if (status === 'received') {
      await openReceiveLinker(po);
      return;
    }
    try {
      await axios.post(`${API_URL}/api/procurement/purchase-orders/${po.id}/status`, { status }, { headers });
      toast.success(`PO marked ${status}`);
      await Promise.all([loadPOs(), loadVatSummary(), loadStockCatalog(), loadExpansionProjects(), loadDashboard()]);
    } catch (err) { toast.error(getDetailMsg(err) || 'Update failed'); }
  };

  const openReceiveLinker = async (po) => {
    setLinkerPO(po);
    setLinkerLines([]);
    setLinkerLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/procurement/purchase-orders/${po.id}/receive-preview`, { headers });
      const lines = (res.data?.stock_lines || []).map(l => ({
        ...l,
        mode: l.item_type === 'expansion' ? 'expansion-done' : (l.linked_to ? 'linked' : 'pick'),
        pick_id: l.stock_product_id || '',
        new_name: l.name || '',
        new_category: 'Procurement',
        new_cost: l.unit_price || 0,
        new_expansion_project_id: l.expansion_project_id || '',
        searching: '',
      }));
      setLinkerLines(lines);
      setLinkerProjects(res.data?.expansion_projects || []);
    } catch (err) {
      toast.error(getDetailMsg(err) || 'Failed to load receive preview');
      setLinkerPO(null);
    } finally { setLinkerLoading(false); }
  };

  const updateLinkerLine = (i, patch) => setLinkerLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const saveLinkerLine = async (i) => {
    const line = linkerLines[i];
    try {
      let payload;
      if (line.mode === 'expansion') {
        if (!line.new_expansion_project_id) { toast.error('Pick an expansion project'); return; }
        payload = { line_index: line.line_index, action: 'mark_expansion', expansion_project_id: line.new_expansion_project_id };
      } else if (line.mode === 'pick') {
        if (!line.pick_id) { toast.error('Pick an internal stock item first'); return; }
        payload = { line_index: line.line_index, action: 'link', stock_product_id: line.pick_id };
      } else {
        if (!(line.new_name || '').trim()) { toast.error('Enter a name for the new stock item'); return; }
        payload = {
          line_index: line.line_index,
          action: 'create',
          new_product: {
            name: line.new_name.trim(),
            category: line.new_category || 'Procurement',
            cost_price: parseFloat(line.new_cost) || 0,
          },
        };
      }
      await axios.post(`${API_URL}/api/procurement/purchase-orders/${linkerPO.id}/link-line`, payload, { headers });
      toast.success(line.mode === 'expansion' ? `Line ${i + 1} converted to non-stock` : `Line ${i + 1} linked`);
      const res = await axios.get(`${API_URL}/api/procurement/purchase-orders/${linkerPO.id}/receive-preview`, { headers });
      const fresh = (res.data?.stock_lines || []).map(l => {
        const old = linkerLines.find(x => x.line_index === l.line_index) || {};
        return {
          ...l,
          mode: l.item_type === 'expansion' ? 'expansion-done' : (l.linked_to ? 'linked' : (old.mode || 'pick')),
          pick_id: l.stock_product_id || old.pick_id || '',
          new_name: old.new_name || l.name || '',
          new_category: old.new_category || 'Procurement',
          new_cost: old.new_cost || l.unit_price || 0,
          new_expansion_project_id: l.expansion_project_id || old.new_expansion_project_id || '',
          searching: old.searching || '',
        };
      });
      setLinkerLines(fresh);
      setLinkerProjects(res.data?.expansion_projects || linkerProjects);
      await loadStockCatalog();
    } catch (err) { toast.error(getDetailMsg(err) || 'Link failed'); }
  };

  const confirmReceive = async () => {
    const unlinked = linkerLines.filter(l => l.item_type === 'stock' && !l.linked_to).length;
    if (unlinked > 0) {
      toast.error(`${unlinked} stock line(s) still need to be linked, created, or marked as non-stock`);
      return;
    }
    try {
      await axios.post(`${API_URL}/api/procurement/purchase-orders/${linkerPO.id}/status`, { status: 'received' }, { headers });
      toast.success('PO marked received — inventory updated');
      setLinkerPO(null);
      setLinkerLines([]);
      await Promise.all([loadPOs(), loadVatSummary(), loadStockCatalog(), loadExpansionProjects(), loadDashboard()]);
    } catch (err) {
      toast.error(getDetailMsg(err) || 'Receive failed');
    }
  };

  const deletePO = async (po) => {
    if (!window.confirm(`Delete ${po.po_number}? Any linked stock-purchase entry will also be removed.`)) return;
    try {
      await axios.delete(`${API_URL}/api/procurement/purchase-orders/${po.id}`, { headers });
      toast.success('PO deleted');
      await Promise.all([loadPOs(), loadVatSummary(), loadDashboard()]);
    } catch { toast.error('Delete failed'); }
  };

  const downloadPO = async (po) => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/purchase-orders/${po.id}/pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${po.po_number}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  // ============ MARKUP REVIEW (admin) ============
  const openMarkup = async (po) => {
    setMarkupPO(po);
    setMarkupLoading(true);
    setMarkupLines([]);
    try {
      const res = await axios.get(`${API_URL}/api/procurement/purchase-orders/${po.id}/markup-lines`, { headers });
      const lines = (res.data?.lines || []).map(l => ({
        ...l,
        selling_price: Number(l.current_selling_price) || 0,
        markup_pct: l.cost_ex_vat > 0
          ? Math.round(((Number(l.current_selling_price) || 0) / Number(l.cost_ex_vat) - 1) * 1000) / 10
          : 0,
      }));
      setMarkupLines(lines);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load markup lines');
      setMarkupPO(null);
    } finally {
      setMarkupLoading(false);
    }
  };
  const updateMarkupLine = (idx, patch) => setMarkupLines(prev => prev.map((l, i) => {
    if (i !== idx) return l;
    const next = { ...l, ...patch };
    const cost = Number(next.cost_ex_vat) || 0;
    if ('markup_pct' in patch && cost > 0) {
      next.selling_price = Math.round(cost * (1 + (Number(next.markup_pct) || 0) / 100) * 100) / 100;
    } else if ('selling_price' in patch && cost > 0) {
      next.markup_pct = Math.round(((Number(next.selling_price) || 0) / cost - 1) * 1000) / 10;
    }
    return next;
  }));
  const saveMarkup = async () => {
    const payload = {
      lines: markupLines
        .filter(l => l.product_id)
        .map(l => ({ product_id: l.product_id, selling_price: Number(l.selling_price) || 0 })),
    };
    if (!payload.lines.length) { toast.error('Nothing to save'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/procurement/purchase-orders/${markupPO.id}/markup`, payload, { headers });
      toast.success(`Updated selling prices on ${res.data.updated} item(s)`);
      setMarkupPO(null);
      setMarkupLines([]);
      await loadStockCatalog();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save markup'); }
  };

  // ============ RENDER ============
  return (
    <div className="space-y-6" data-testid="procurement-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="h-6 w-6 text-blue-400" /> Procurement
          </h1>
          <p className="text-sm text-slate-400">Manage suppliers, their product catalogs, and purchase orders. Receiving a PO auto-flows the cost into Cash-Up.</p>
        </div>
      </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-violet-600 text-white" data-testid="tab-dashboard">
              <ListChecks className="h-4 w-4 mr-1.5" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="suppliers" className="data-[state=active]:bg-blue-600 text-white" data-testid="tab-suppliers">
              <Building2 className="h-4 w-4 mr-1.5" /> Suppliers
            </TabsTrigger>
            <TabsTrigger value="purchase-orders" className="data-[state=active]:bg-emerald-600 text-white" data-testid="tab-purchase-orders">
              <ListChecks className="h-4 w-4 mr-1.5" /> Purchase Orders
            </TabsTrigger>
            <TabsTrigger value="vat-summary" className="data-[state=active]:bg-amber-600 text-white" data-testid="tab-vat-summary">
              <Building2 className="h-4 w-4 mr-1.5" /> VAT / SARS
            </TabsTrigger>
          </TabsList>

          {/* ============ DASHBOARD TAB ============ */}
          <TabsContent value="dashboard" className="space-y-4">
            {!dashboard ? (
              <div className="text-center py-8 text-slate-400">Loading dashboard...</div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-kpi-tiles">
                  <div className="rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-4">
                    <div className="text-xs opacity-80 uppercase tracking-wide">{dashboard.current_month} · POs</div>
                    <p className="text-3xl font-bold">{dashboard.this_month.po_count}</p>
                    <div className="text-[10px] opacity-70 mt-1">{R(dashboard.this_month.total_incl_vat)} incl VAT</div>
                  </div>
                  <div className="rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white p-4">
                    <div className="text-xs opacity-80 uppercase tracking-wide">All-Time POs</div>
                    <p className="text-3xl font-bold">{dashboard.all_time.po_count}</p>
                    <div className="text-[10px] opacity-70 mt-1">{R(dashboard.all_time.total_incl_vat)} cumulative</div>
                  </div>
                  <div className="rounded-lg bg-gradient-to-br from-amber-600 to-amber-700 text-white p-4">
                    <div className="text-xs opacity-80 uppercase tracking-wide">This Month Ex VAT</div>
                    <p className="text-3xl font-bold">{R(dashboard.this_month.total_ex_vat)}</p>
                    <div className="text-[10px] opacity-70 mt-1">Procurement spend</div>
                  </div>
                  <div className="rounded-lg bg-gradient-to-br from-slate-600 to-slate-700 text-white p-4">
                    <div className="text-xs opacity-80 uppercase tracking-wide">Received This Month</div>
                    <p className="text-3xl font-bold">{R(dashboard.trend[dashboard.trend.length - 1]?.received_total)}</p>
                    <div className="text-[10px] opacity-70 mt-1">Auto-flowed to inventory</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Card className="bg-slate-900 border-slate-700">
                    <CardHeader className="py-3"><CardTitle className="text-base text-white">PO Status (all time)</CardTitle></CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader><TableRow className="border-slate-700 bg-slate-800/60">
                          <TableHead className="text-slate-400">Status</TableHead>
                          <TableHead className="text-slate-400 text-right">Count</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {['draft', 'sent', 'received', 'cancelled'].map(s => (
                            <TableRow key={s} className="border-slate-700">
                              <TableCell><Badge className={STATUS_STYLES[s]}>{s.toUpperCase()}</Badge></TableCell>
                              <TableCell className="text-right text-white font-semibold">{dashboard.all_time.by_status[s] || 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-900 border-slate-700">
                    <CardHeader className="py-3"><CardTitle className="text-base text-white">Payment Source · {dashboard.current_month}</CardTitle></CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader><TableRow className="border-slate-700 bg-slate-800/60">
                          <TableHead className="text-slate-400">Source</TableHead>
                          <TableHead className="text-slate-400 text-right">POs</TableHead>
                          <TableHead className="text-slate-400 text-right">Total</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {['bank', 'eft', 'card', 'cash'].map(src => (
                            <TableRow key={src} className="border-slate-700">
                              <TableCell className="text-white font-medium uppercase">{src}</TableCell>
                              <TableCell className="text-right text-slate-200">{dashboard.this_month.by_payment_source[src]?.count || 0}</TableCell>
                              <TableCell className="text-right text-emerald-400 font-semibold">{R(dashboard.this_month.by_payment_source[src]?.total)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                <Card className="bg-slate-900 border-slate-700">
                  <CardHeader className="py-3"><CardTitle className="text-base text-white">6-Month Trend</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader><TableRow className="border-slate-700 bg-slate-800/60">
                        <TableHead className="text-slate-400">Month</TableHead>
                        <TableHead className="text-slate-400 text-right">POs Created</TableHead>
                        <TableHead className="text-slate-400 text-right">Total Spend</TableHead>
                        <TableHead className="text-slate-400 text-right">Received Total</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {dashboard.trend.map(t => (
                          <TableRow key={t.month} className="border-slate-700">
                            <TableCell className="text-white font-mono">{t.month}</TableCell>
                            <TableCell className="text-right text-slate-200">{t.po_count}</TableCell>
                            <TableCell className="text-right text-blue-400 font-semibold">{R(t.total_incl_vat)}</TableCell>
                            <TableCell className="text-right text-emerald-400">{R(t.received_total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {dashboard.top_suppliers?.length > 0 && (
                  <Card className="bg-slate-900 border-slate-700">
                    <CardHeader className="py-3"><CardTitle className="text-base text-white">Top Suppliers (all-time)</CardTitle></CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader><TableRow className="border-slate-700 bg-slate-800/60">
                          <TableHead className="text-slate-400">Supplier</TableHead>
                          <TableHead className="text-slate-400 text-right">POs</TableHead>
                          <TableHead className="text-slate-400 text-right">Total Spend</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {dashboard.top_suppliers.map(s => (
                            <TableRow key={s.supplier} className="border-slate-700">
                              <TableCell className="text-white">{s.supplier}</TableCell>
                              <TableCell className="text-right text-slate-200">{s.po_count}</TableCell>
                              <TableCell className="text-right text-emerald-400 font-semibold">{R(s.total_incl_vat)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* ============ SUPPLIERS TAB ============ */}
          <TabsContent value="suppliers" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Suppliers list */}
              <Card className="bg-slate-900 border-slate-700 lg:col-span-2" data-testid="suppliers-list-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                  <CardTitle className="text-base text-white">{suppliers.length} Supplier{suppliers.length === 1 ? '' : 's'}</CardTitle>
                  <Button size="sm" onClick={openNewSupplier} className="bg-blue-600 hover:bg-blue-700" data-testid="new-supplier-btn">
                    <Plus className="h-4 w-4 mr-1" /> New
                  </Button>
                </CardHeader>
                <CardContent>
                  {suppliers.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">No suppliers yet — click <b>New</b> to add one.</div>
                  ) : (
                    <div className="space-y-1">
                      {suppliers.map(s => (
                        <div
                          key={s.id}
                          onClick={() => setActiveSupplier(s)}
                          className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md cursor-pointer transition ${activeSupplier?.id === s.id ? 'bg-blue-950/60 border border-blue-700' : 'hover:bg-slate-800 border border-transparent'}`}
                          data-testid={`supplier-row-${s.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-white truncate">{s.company_name}</div>
                            <div className="text-xs text-slate-400 truncate">{s.email || '—'}</div>
                          </div>
                          <Badge className="bg-slate-700 text-slate-200">{s.product_count} items</Badge>
                          <ChevronRight className="h-4 w-4 text-slate-500" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Active supplier products */}
              <Card className="bg-slate-900 border-slate-700 lg:col-span-3" data-testid="active-supplier-card">
                <CardHeader className="py-3">
                  {activeSupplier ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base text-white">{activeSupplier.company_name}</CardTitle>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {activeSupplier.email || '—'} {activeSupplier.phone ? `· ${activeSupplier.phone}` : ''}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditSupplier(activeSupplier)} className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700" data-testid="edit-supplier-btn">
                          <Pencil className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteSupplier(activeSupplier)} className="bg-slate-800 border-rose-800 text-rose-400 hover:bg-rose-950">
                          <Trash2 className="h-4 w-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <CardTitle className="text-base text-slate-400">Select a supplier on the left to view their products.</CardTitle>
                  )}
                </CardHeader>
                {activeSupplier && (
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-medium flex items-center gap-1.5"><Package className="h-4 w-4 text-amber-400" /> Products they sell</h3>
                      <Button size="sm" onClick={openNewProduct} className="bg-amber-600 hover:bg-amber-700" data-testid="new-product-btn">
                        <Plus className="h-4 w-4 mr-1" /> Add Product
                      </Button>
                    </div>
                    {supplierProducts.length === 0 ? (
                      <div className="text-center py-6 text-slate-400 text-sm">No products yet — add the items this supplier sells with their prices.</div>
                    ) : (
                      <div className="rounded-lg border border-slate-700 overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-slate-700 bg-slate-800/60">
                              <TableHead className="text-slate-400">SKU</TableHead>
                              <TableHead className="text-slate-400">Item</TableHead>
                              <TableHead className="text-slate-400 text-right">Price</TableHead>
                              <TableHead className="text-slate-400 text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {supplierProducts.map(p => (
                              <TableRow key={p.id} className="border-slate-700">
                                <TableCell className="font-mono text-xs text-slate-300">{p.sku || '—'}</TableCell>
                                <TableCell className="text-white">
                                  {p.name}
                                  {p.notes && <div className="text-xs text-slate-500">{p.notes}</div>}
                                </TableCell>
                                <TableCell className="text-right text-emerald-400 font-semibold">{R(p.price)}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Button size="sm" className="h-7 w-7 p-0 bg-blue-600 hover:bg-blue-700" onClick={() => openEditProduct(p)} data-testid={`edit-product-${p.id}`}>
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button size="sm" className="h-7 w-7 p-0 bg-rose-600 hover:bg-rose-700" onClick={() => deleteProduct(p)}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ============ PURCHASE ORDERS TAB ============ */}
          <TabsContent value="purchase-orders" className="space-y-4">
            {/* VAT quick-stats tiles right above the PO table */}
            {vatSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vat-quick-tiles">
                <div className="rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-3">
                  <div className="text-xs opacity-80 uppercase tracking-wide">Claimable VAT (Bank)</div>
                  <p className="text-xl font-bold">{R(vatSummary.this_month.claimable_vat)}</p>
                  <div className="text-[10px] opacity-70 mt-0.5">{vatSummary.this_month.po_count_claimable} POs · this month</div>
                </div>
                <div className="rounded-lg bg-gradient-to-br from-slate-600 to-slate-700 text-white p-3">
                  <div className="text-xs opacity-80 uppercase tracking-wide">Non-Claimable VAT (Cash)</div>
                  <p className="text-xl font-bold">{R(vatSummary.this_month.non_claimable_vat)}</p>
                  <div className="text-[10px] opacity-70 mt-0.5">{vatSummary.this_month.po_count_non_claimable} POs · this month</div>
                </div>
                <div className="rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white p-3">
                  <div className="text-xs opacity-80 uppercase tracking-wide">All-Time Claimable</div>
                  <p className="text-xl font-bold">{R(vatSummary.all_time.claimable_vat)}</p>
                </div>
                <div className="rounded-lg bg-gradient-to-br from-amber-600 to-amber-700 text-white p-3">
                  <div className="text-xs opacity-80 uppercase tracking-wide">All-Time Non-Claimable</div>
                  <p className="text-xl font-bold">{R(vatSummary.all_time.non_claimable_vat)}</p>
                </div>
              </div>
            )}

            <Card className="bg-slate-900 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <CardTitle className="text-base text-white">{pos.length} Purchase Order{pos.length === 1 ? '' : 's'}</CardTitle>
                <Button size="sm" onClick={openNewPO} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-po-btn">
                  <Plus className="h-4 w-4 mr-1" /> Create PO
                </Button>
              </CardHeader>
              <CardContent>
                {pos.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">No purchase orders yet.</div>
                ) : (
                  <div className="rounded-lg border border-slate-700 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700 bg-slate-800/60">
                          <TableHead className="text-slate-400">PO #</TableHead>
                          <TableHead className="text-slate-400">Supplier</TableHead>
                          <TableHead className="text-slate-400">Date</TableHead>
                          <TableHead className="text-slate-400 text-center">Lines</TableHead>
                          <TableHead className="text-slate-400 text-right">Ex VAT</TableHead>
                          <TableHead className="text-slate-400 text-right">VAT</TableHead>
                          <TableHead className="text-slate-400 text-right">Total</TableHead>
                          <TableHead className="text-slate-400 text-center">Source</TableHead>
                          <TableHead className="text-slate-400">Status</TableHead>
                          <TableHead className="text-slate-400 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pos.map(po => (
                          <TableRow key={po.id} className="border-slate-700">
                            <TableCell className="font-mono text-xs text-blue-400 cursor-pointer hover:underline" onClick={() => setActivePO(po)} data-testid={`po-row-${po.po_number}`}>
                              {po.po_number}
                            </TableCell>
                            <TableCell className="text-white">{po.supplier_snapshot?.company_name || '—'}</TableCell>
                            <TableCell className="text-slate-300 text-xs">{(po.created_at || '').slice(0, 10)}</TableCell>
                            <TableCell className="text-center text-slate-300">{po.line_items?.length || 0}</TableCell>
                            <TableCell className="text-right text-slate-200">{R(po.subtotal_ex_vat ?? po.subtotal)}</TableCell>
                            <TableCell className="text-right text-amber-400">{R(po.vat_amount || 0)}</TableCell>
                            <TableCell className="text-right text-emerald-400 font-semibold">{R(po.total_incl_vat ?? po.subtotal)}</TableCell>
                            <TableCell className="text-center">
                              <Select
                                value={po.payment_source || 'cash'}
                                onValueChange={(v) => changePaymentSource(po, v)}
                              >
                                <SelectTrigger
                                  className={`h-7 px-2 text-[10px] font-bold border w-[80px] ${po.payment_source === 'cash' ? 'bg-slate-700 border-slate-600 text-white' : 'bg-blue-700 border-blue-500 text-white'}`}
                                  data-testid={`po-source-trigger-${po.po_number}`}
                                  title={po.payment_source === 'cash' ? 'Cash — VAT NOT claimable' : 'Bank-paid — VAT claimable'}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-slate-700">
                                  <SelectItem value="bank">BANK</SelectItem>
                                  <SelectItem value="eft">EFT</SelectItem>
                                  <SelectItem value="card">CARD</SelectItem>
                                  <SelectItem value="cash">CASH</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Badge className={STATUS_STYLES[po.status] || 'bg-slate-700 text-white'}>{po.status?.toUpperCase()}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1 flex-wrap">
                                <Button size="sm" className="h-7 px-2 bg-slate-700 hover:bg-slate-600 text-white" onClick={() => downloadPO(po)} title="Download PDF" data-testid={`download-po-${po.po_number}`}>
                                  <FileDown className="h-3 w-3" />
                                </Button>
                                {isAdmin && po.status === 'received' && (
                                  <Button size="sm" className="h-7 px-2 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => openMarkup(po)} title="Set Selling Price / Markup" data-testid={`markup-po-${po.po_number}`}>
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                )}
                                {po.status === 'draft' && (
                                  <Button size="sm" className="h-7 px-2 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => changeStatus(po, 'sent')} title="Mark as Sent">
                                    <Send className="h-3 w-3" />
                                  </Button>
                                )}
                                {(po.status === 'draft' || po.status === 'sent') && (
                                  <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => changeStatus(po, 'received')} title="Mark as Received">
                                    <CheckCircle2 className="h-3 w-3" />
                                  </Button>
                                )}
                                {po.status !== 'cancelled' && (
                                  <Button size="sm" className="h-7 px-2 bg-amber-700 hover:bg-amber-800 text-white" onClick={() => changeStatus(po, 'cancelled')} title="Cancel PO">
                                    <XCircle className="h-3 w-3" />
                                  </Button>
                                )}
                                <Button size="sm" className="h-7 px-2 bg-rose-700 hover:bg-rose-800 text-white" onClick={() => deletePO(po)} title="Delete PO">
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ VAT / SARS SUMMARY TAB ============ */}
          <TabsContent value="vat-summary" className="space-y-4">
            {!vatSummary ? (
              <div className="text-center py-8 text-slate-400">Loading VAT summary...</div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-4" data-testid="vat-tile-claim-month">
                    <div className="text-xs opacity-80 uppercase tracking-wide">{vatSummary.current_month} · Claimable</div>
                    <p className="text-2xl font-bold">{R(vatSummary.this_month.claimable_vat)}</p>
                    <div className="text-[10px] opacity-70 mt-1">Ex VAT base: {R(vatSummary.this_month.claimable_ex_vat)}</div>
                    <div className="text-[10px] opacity-70">{vatSummary.this_month.po_count_claimable} POs paid from bank</div>
                  </div>
                  <div className="rounded-lg bg-gradient-to-br from-slate-600 to-slate-700 text-white p-4" data-testid="vat-tile-nonclaim-month">
                    <div className="text-xs opacity-80 uppercase tracking-wide">{vatSummary.current_month} · Non-Claimable</div>
                    <p className="text-2xl font-bold">{R(vatSummary.this_month.non_claimable_vat)}</p>
                    <div className="text-[10px] opacity-70 mt-1">Ex VAT base: {R(vatSummary.this_month.non_claimable_ex_vat)}</div>
                    <div className="text-[10px] opacity-70">{vatSummary.this_month.po_count_non_claimable} cash POs</div>
                  </div>
                  <div className="rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white p-4">
                    <div className="text-xs opacity-80 uppercase tracking-wide">All-Time Claimable</div>
                    <p className="text-2xl font-bold">{R(vatSummary.all_time.claimable_vat)}</p>
                    <div className="text-[10px] opacity-70 mt-1">Total submitted to SARS</div>
                  </div>
                  <div className="rounded-lg bg-gradient-to-br from-amber-600 to-amber-700 text-white p-4">
                    <div className="text-xs opacity-80 uppercase tracking-wide">All-Time Non-Claimable</div>
                    <p className="text-2xl font-bold">{R(vatSummary.all_time.non_claimable_vat)}</p>
                    <div className="text-[10px] opacity-70 mt-1">Cash-paid VAT (lost)</div>
                  </div>
                </div>

                {/* 6-month trend */}
                <Card className="bg-slate-900 border-slate-700">
                  <CardHeader className="py-3"><CardTitle className="text-base text-white">6-Month VAT Trend</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700 bg-slate-800/60">
                          <TableHead className="text-slate-400">Month</TableHead>
                          <TableHead className="text-slate-400 text-right">Claimable VAT</TableHead>
                          <TableHead className="text-slate-400 text-right">Non-Claimable VAT</TableHead>
                          <TableHead className="text-slate-400 text-right">Total VAT Paid</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vatSummary.trend.map(t => (
                          <TableRow key={t.month} className="border-slate-700">
                            <TableCell className="text-white font-mono">{t.month}</TableCell>
                            <TableCell className="text-right text-emerald-400 font-semibold">{R(t.claimable_vat)}</TableCell>
                            <TableCell className="text-right text-slate-300">{R(t.non_claimable_vat)}</TableCell>
                            <TableCell className="text-right text-amber-300">{R((t.claimable_vat || 0) + (t.non_claimable_vat || 0))}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Detailed claimable rows for THIS MONTH (what the accountant submits) */}
                <Card className="bg-slate-900 border-slate-700">
                  <CardHeader className="py-3">
                    <CardTitle className="text-base text-white">Claimable Input VAT — {vatSummary.current_month}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(vatSummary.current_month_claimable_rows || []).length === 0 ? (
                      <div className="text-center py-6 text-slate-400">No bank-paid POs this month — nothing to claim yet.</div>
                    ) : (
                      <div className="rounded-lg border border-slate-700 overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-slate-700 bg-slate-800/60">
                              <TableHead className="text-slate-400">Date</TableHead>
                              <TableHead className="text-slate-400">Supplier</TableHead>
                              <TableHead className="text-slate-400">PO #</TableHead>
                              <TableHead className="text-slate-400 text-right">Amount Ex VAT</TableHead>
                              <TableHead className="text-slate-400 text-right">VAT @ {vatSummary.current_month_claimable_rows[0]?.vat_rate || 15}%</TableHead>
                              <TableHead className="text-slate-400 text-right">Total Incl VAT</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {vatSummary.current_month_claimable_rows.map(r => (
                              <TableRow key={r.id} className="border-slate-700">
                                <TableCell className="text-slate-300 font-mono text-xs">{r.date}</TableCell>
                                <TableCell className="text-white">{r.supplier_name}</TableCell>
                                <TableCell className="text-blue-400 font-mono text-xs">{r.po_number || '—'}</TableCell>
                                <TableCell className="text-right text-slate-200">{R(r.amount_ex_vat)}</TableCell>
                                <TableCell className="text-right text-emerald-400 font-semibold">{R(r.vat_amount)}</TableCell>
                                <TableCell className="text-right text-slate-300">{R((r.amount_ex_vat || 0) + (r.vat_amount || 0))}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* ============ Supplier Dialog ============ */}
        <Dialog open={showSupplierDlg} onOpenChange={setShowSupplierDlg}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="supplier-dialog">
            <DialogHeader>
              <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'New Supplier'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300 text-xs">Company Name *</Label>
                <Input value={supplierForm.company_name} onChange={(e) => setSupplierForm({ ...supplierForm, company_name: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="supplier-company-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Email</Label>
                  <Input type="email" value={supplierForm.email} onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} className="bg-slate-800 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Phone</Label>
                  <Input value={supplierForm.phone} onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })} className="bg-slate-800 border-slate-700" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Address</Label>
                <Textarea value={supplierForm.address} onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })} rows={2} className="bg-slate-800 border-slate-700" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Notes</Label>
                <Textarea value={supplierForm.notes} onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })} rows={2} className="bg-slate-800 border-slate-700" />
              </div>
              <Button onClick={saveSupplier} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="save-supplier-btn">Save</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ============ Product Dialog ============ */}
        <Dialog open={showProductDlg} onOpenChange={setShowProductDlg}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="product-dialog">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'New Product'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300 text-xs">Item Name *</Label>
                <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="product-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Supplier SKU</Label>
                  <Input value={productForm.sku} onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="optional" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Price (R) *</Label>
                  <Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="product-price-input" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Notes</Label>
                <Textarea value={productForm.notes} onChange={(e) => setProductForm({ ...productForm, notes: e.target.value })} rows={2} className="bg-slate-800 border-slate-700" />
              </div>
              <Button onClick={saveProduct} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="save-product-btn">Save</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ============ PO Creator Dialog ============ */}
        <Dialog open={showPOCreator} onOpenChange={setShowPOCreator}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl" data-testid="po-creator-dialog">
            <DialogHeader>
              <DialogTitle>Create Purchase Order</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300 text-xs">Supplier *</Label>
                <Select value={poDraft.supplier_id} onValueChange={onPickPOSupplier}>
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="po-supplier-select">
                    <SelectValue placeholder="Pick a supplier..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {poDraft.supplier_id && (
                <div className="space-y-3" data-testid="po-line-builder">
                  {/* Add-line action bar */}
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/40 p-2">
                    <div className="flex-1 min-w-[180px]">
                      <Input
                        value={stockSearch}
                        onChange={(e) => setStockSearch(e.target.value)}
                        placeholder="Search stock by name or code..."
                        className="bg-slate-900 border-slate-700 h-8 text-sm"
                        data-testid="po-stock-search"
                      />
                    </div>
                    <Button
                      size="sm"
                      type="button"
                      onClick={addExpansionLine}
                      className="bg-purple-600 hover:bg-purple-700 h-8"
                      data-testid="po-add-expansion-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Expansion (non-stock)
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Stock catalog picker (filtered) */}
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="py-2">
                        <CardTitle className="text-sm text-white flex items-center gap-1.5">
                          <Package className="h-3.5 w-3.5 text-emerald-400" /> Stock Catalog
                          <span className="text-[10px] text-slate-400 font-normal">(click to add &amp; link by code)</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-2 max-h-72 overflow-y-auto space-y-1">
                        {(() => {
                          const term = (stockSearch || '').trim().toLowerCase();
                          const filtered = term
                            ? stockCatalog.filter(p =>
                                (p.name || '').toLowerCase().includes(term) ||
                                (p.id || '').toLowerCase().includes(term) ||
                                (p.sku || '').toLowerCase().includes(term) ||
                                (p.supplier_sku || '').toLowerCase().includes(term))
                            : stockCatalog;
                          if (!filtered.length) {
                            return <div className="text-center text-xs text-slate-400 py-4">No matches. Start typing to search the {stockCatalog.length}-item catalog.</div>;
                          }
                          return filtered.slice(0, 50).map(p => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => addStockLine(p)}
                              className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-700 flex items-center gap-2"
                              data-testid={`po-stock-add-${p.id}`}
                            >
                              <Plus className="h-3 w-3 text-emerald-400 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-mono text-slate-500 truncate">
                                  {p.sku ? `SKU ${p.sku}` : `CODE ${p.id.slice(0, 8)}`}
                                  {typeof p.on_hand === 'number' ? ` · QTY ${p.on_hand}` : ''}
                                </div>
                                <div className="text-sm text-white truncate">{p.name}</div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="text-emerald-400 font-semibold text-xs">{R(p.default_cost ?? p.cost_price)}</div>
                                <div className="text-[9px] text-slate-500">est. cost</div>
                              </div>
                            </button>
                          ));
                        })()}
                      </CardContent>
                    </Card>

                    {/* Line items list (mix of stock + expansion) */}
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="py-2">
                        <CardTitle className="text-sm text-white">PO Line Items ({poDraft.line_items.length})</CardTitle>
                      </CardHeader>
                      <CardContent className="p-2 max-h-72 overflow-y-auto space-y-2">
                        {poDraft.line_items.length === 0 ? (
                          <div className="text-center text-xs text-slate-400 py-4">
                            Pick a stock item from the catalog on the left, or click <b>Expansion (non-stock)</b> for a non-stock buy.
                          </div>
                        ) : poDraft.line_items.map((li, idx) => (
                          <div
                            key={`${li.product_id}-${idx}`}
                            className={`rounded border p-2 space-y-1.5 ${li.item_type === 'expansion' ? 'bg-purple-950/40 border-purple-700' : 'bg-slate-900 border-slate-700'}`}
                            data-testid={`po-line-${idx}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <Badge className={li.item_type === 'expansion' ? 'bg-purple-600 text-white text-[9px]' : 'bg-emerald-600 text-white text-[9px]'}>
                                    {li.item_type === 'expansion' ? 'EXPANSION' : 'STOCK'}
                                  </Badge>
                                  {li.stock_product_id && (
                                    <span className="text-[10px] font-mono text-slate-400" title="Linked stock code">
                                      #{li.stock_product_id.slice(0, 8)}
                                    </span>
                                  )}
                                </div>
                                {li.item_type === 'expansion' ? (
                                  <Input
                                    value={li.name}
                                    onChange={(e) => updateLineItem(idx, { name: e.target.value })}
                                    placeholder="Item description (e.g. Branch signage)"
                                    className="bg-slate-800 border-slate-700 h-7 text-sm mt-1"
                                    data-testid={`po-line-name-${idx}`}
                                  />
                                ) : (
                                  <div className="text-sm text-white mt-0.5">{li.name}</div>
                                )}
                              </div>
                              <Button size="sm" className="h-6 w-6 p-0 bg-rose-700 hover:bg-rose-800" onClick={() => removeLineItem(idx)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>

                            {li.item_type === 'expansion' && (
                              <div>
                                <Label className="text-[10px] text-slate-400">Expansion Project *</Label>
                                <Select
                                  value={li.expansion_project_id || ''}
                                  onValueChange={(v) => updateLineItem(idx, { expansion_project_id: v })}
                                >
                                  <SelectTrigger className="bg-slate-800 border-slate-700 h-7 text-xs" data-testid={`po-line-project-${idx}`}>
                                    <SelectValue placeholder="Pick a project..." />
                                  </SelectTrigger>
                                  <SelectContent className="bg-slate-800 border-slate-700">
                                    {expansionProjects.length === 0 ? (
                                      <div className="px-2 py-1 text-xs text-slate-400">No active projects — add one in the Expansion Budget tab.</div>
                                    ) : expansionProjects.map(p => (
                                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-[10px] text-slate-500">Qty</Label>
                                <Input type="number" min="1" value={li.quantity} onChange={(e) => updateLineItem(idx, { quantity: parseInt(e.target.value, 10) || 0 })} className="bg-slate-800 border-slate-700 h-7 text-sm" />
                              </div>
                              <div>
                                <Label className="text-[10px] text-slate-500">Unit R (ex VAT)</Label>
                                <Input type="number" step="0.01" value={li.unit_price} onChange={(e) => updateLineItem(idx, { unit_price: parseFloat(e.target.value) || 0 })} className="bg-slate-800 border-slate-700 h-7 text-sm" />
                              </div>
                            </div>
                            <div className="text-right text-xs text-emerald-400 font-semibold">{R((Number(li.quantity) || 0) * (Number(li.unit_price) || 0))}</div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Optional legacy supplier-product shortlist (collapsible) */}
                  {poDraftSupplierProducts.length > 0 && (
                    <details className="rounded-lg border border-slate-700 bg-slate-800/30 p-2">
                      <summary className="cursor-pointer text-xs text-slate-400 select-none">
                        Suggested items from this supplier's catalog ({poDraftSupplierProducts.length})
                      </summary>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-2 max-h-40 overflow-y-auto">
                        {poDraftSupplierProducts.map(p => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => addLineItem(p)}
                            className="text-left px-2 py-1.5 rounded hover:bg-slate-700 flex items-center gap-2"
                            data-testid={`po-add-product-${p.id}`}
                          >
                            <Plus className="h-3 w-3 text-blue-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] font-mono text-slate-500 truncate">{p.sku || '—'}</div>
                              <div className="text-xs text-white truncate">{p.name}</div>
                            </div>
                            <div className="text-emerald-400 font-semibold text-xs">{R(p.price)}</div>
                          </button>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              <div>
                <Label className="text-slate-300 text-xs">Notes (optional)</Label>
                <Textarea value={poDraft.notes} onChange={(e) => setPoDraft({ ...poDraft, notes: e.target.value })} rows={2} className="bg-slate-800 border-slate-700" placeholder="Delivery instructions, urgency, etc." />
              </div>

              {/* VAT + Payment Source */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-lg border border-slate-700 bg-slate-800/40 p-3">
                <div>
                  <Label className="text-slate-300 text-xs">VAT Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={poDraft.vat_rate}
                    onChange={(e) => setPoDraft({ ...poDraft, vat_rate: e.target.value })}
                    className="bg-slate-900 border-slate-700"
                    data-testid="po-vat-rate-input"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">Default 15% (SA standard). Use 0% for zero-rated.</div>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Payment Source *</Label>
                  <div className="grid grid-cols-4 gap-2 mt-1">
                    {[
                      { v: 'bank', label: 'Bank',  desc: 'VAT claimable', cls: 'bg-blue-600 hover:bg-blue-700 border-blue-500' },
                      { v: 'eft',  label: 'EFT',   desc: 'VAT claimable', cls: 'bg-indigo-600 hover:bg-indigo-700 border-indigo-500' },
                      { v: 'card', label: 'Card',  desc: 'VAT claimable', cls: 'bg-purple-600 hover:bg-purple-700 border-purple-500' },
                      { v: 'cash', label: 'Cash',  desc: 'NOT claimable', cls: 'bg-slate-600 hover:bg-slate-700 border-slate-500' },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setPoDraft({ ...poDraft, payment_source: opt.v })}
                        data-testid={`po-source-${opt.v}`}
                        className={`rounded-md border px-2 py-2 text-xs font-semibold text-white transition ${poDraft.payment_source === opt.v ? opt.cls + ' ring-2 ring-white/40' : 'bg-slate-900 border-slate-700 hover:bg-slate-800'}`}
                      >
                        <div className="font-bold">{opt.label}</div>
                        <div className="text-[9px] opacity-80 font-normal">{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-slate-700 pt-3 gap-3 flex-wrap">
                <div className="text-sm text-slate-300 space-y-0.5">
                  <div>Subtotal (Excl VAT): <span className="text-emerald-400 font-semibold">{R(poSubtotal)}</span></div>
                  <div>VAT @ {Number(poDraft.vat_rate) || 0}%: <span className="text-amber-400 font-semibold">{R(poVat)}</span></div>
                  <div className="text-base text-white">TOTAL (Incl VAT): <span className="text-emerald-300 font-bold">{R(poTotalIncl)}</span></div>
                </div>
                <Button onClick={savePO} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-po-btn">
                  Save Purchase Order
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ============ PO Detail Dialog ============ */}
        <Dialog open={!!activePO} onOpenChange={(o) => { if (!o) setActivePO(null); }}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-mono text-blue-400">
                {activePO?.po_number}
                <Badge className={STATUS_STYLES[activePO?.status] || 'bg-slate-700'}>{(activePO?.status || '').toUpperCase()}</Badge>
              </DialogTitle>
            </DialogHeader>
            {activePO && (
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-slate-400 text-xs uppercase">Supplier</div>
                  <div className="text-white font-semibold">{activePO.supplier_snapshot?.company_name}</div>
                  <div className="text-slate-400 text-xs">
                    {activePO.supplier_snapshot?.email || '—'}{activePO.supplier_snapshot?.phone ? ` · ${activePO.supplier_snapshot.phone}` : ''}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-700 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 bg-slate-800/60">
                        <TableHead className="text-slate-400">SKU</TableHead>
                        <TableHead className="text-slate-400">Item</TableHead>
                        <TableHead className="text-slate-400 text-center">Qty</TableHead>
                        <TableHead className="text-slate-400 text-right">Unit</TableHead>
                        <TableHead className="text-slate-400 text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(activePO.line_items || []).map((li, i) => (
                        <TableRow key={i} className="border-slate-700">
                          <TableCell className="font-mono text-xs">{li.sku || '—'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <Badge className={(li.item_type || 'stock') === 'expansion' ? 'bg-purple-600 text-white text-[9px]' : 'bg-emerald-600 text-white text-[9px]'}>
                                {(li.item_type || 'stock') === 'expansion' ? 'EXP' : 'STK'}
                              </Badge>
                              <span>{li.name}</span>
                            </div>
                            {li.stock_product_id && (
                              <div className="text-[10px] font-mono text-slate-500">code: {li.stock_product_id.slice(0, 8)}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-center">{li.quantity}</TableCell>
                          <TableCell className="text-right">{R(li.unit_price)}</TableCell>
                          <TableCell className="text-right text-emerald-400 font-semibold">{R(li.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-between border-t border-slate-700 pt-3">
                  <div className="text-lg">Subtotal: <span className="text-emerald-400 font-bold">{R(activePO.subtotal)}</span></div>
                  <Button onClick={() => downloadPO(activePO)} className="bg-slate-700 hover:bg-slate-600">
                    <FileDown className="h-4 w-4 mr-1" /> Download PDF
                  </Button>
                </div>
                {activePO.notes && (
                  <div>
                    <div className="text-slate-400 text-xs uppercase">Notes</div>
                    <div className="text-slate-200">{activePO.notes}</div>
                  </div>
                )}
                <div className="text-xs text-slate-500">
                  Created {(activePO.created_at || '').slice(0, 10)} by {activePO.created_by}
                  {activePO.received_at && <> · Received {activePO.received_at.slice(0, 10)} by {activePO.received_by}</>}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ============ Markup Review Dialog (admin only) ============ */}
        <Dialog open={!!markupPO} onOpenChange={(o) => { if (!o) { setMarkupPO(null); setMarkupLines([]); } }}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="markup-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5 text-amber-400" /> Set Selling Prices · {markupPO?.po_number}
                <Badge className="bg-emerald-600 text-white text-[10px]">RECEIVED</Badge>
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-400 -mt-2">
              Type a <b>% markup</b> or a fixed <b>Selling Price</b> — the other field auto-calculates. Hit <b>Save</b> to push retail prices into the Parts Catalog &amp; Inventory.
            </p>
            {markupLoading ? (
              <div className="text-center py-8 text-slate-400">Loading lines…</div>
            ) : markupLines.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No stock lines on this PO to mark up.</div>
            ) : (
              <div className="space-y-2">
                <div className="rounded-lg border border-slate-700 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 bg-slate-800/60">
                        <TableHead className="text-slate-400">Item</TableHead>
                        <TableHead className="text-slate-400 text-right">Cost Ex VAT</TableHead>
                        <TableHead className="text-slate-400 text-right w-28">% Markup</TableHead>
                        <TableHead className="text-slate-400 text-right w-32">Selling Price</TableHead>
                        <TableHead className="text-slate-400 text-right">Profit / Unit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {markupLines.map((l, idx) => {
                        const profit = (Number(l.selling_price) || 0) - (Number(l.cost_ex_vat) || 0);
                        return (
                          <TableRow key={idx} className="border-slate-700" data-testid={`markup-row-${idx}`}>
                            <TableCell className="text-white">
                              <div className="text-sm">{l.po_line_name}</div>
                              <div className="text-[10px] text-slate-500 font-mono">
                                {l.po_line_sku ? `SKU ${l.po_line_sku} · ` : ''}qty {l.quantity}
                                {l.product_id ? '' : ' · ⚠ no linked product'}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-slate-200 font-mono text-xs">{R(l.cost_ex_vat)}</TableCell>
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                step="0.1"
                                value={l.markup_pct}
                                onChange={(e) => updateMarkupLine(idx, { markup_pct: parseFloat(e.target.value) || 0 })}
                                className="bg-slate-800 border-slate-700 h-8 text-right text-sm"
                                disabled={!l.product_id}
                                data-testid={`markup-pct-${idx}`}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                step="0.01"
                                value={l.selling_price}
                                onChange={(e) => updateMarkupLine(idx, { selling_price: parseFloat(e.target.value) || 0 })}
                                className="bg-slate-800 border-slate-700 h-8 text-right text-sm"
                                disabled={!l.product_id}
                                data-testid={`markup-price-${idx}`}
                              />
                            </TableCell>
                            <TableCell className={`text-right text-xs font-semibold ${profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {R(profit)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-between border-t border-slate-700 pt-3">
                  <div className="text-xs text-slate-400">
                    Quick-apply: set all rows to{' '}
                    {[20, 30, 35, 50, 100].map(p => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setMarkupLines(prev => prev.map(l => {
                          const cost = Number(l.cost_ex_vat) || 0;
                          return l.product_id ? { ...l, markup_pct: p, selling_price: Math.round(cost * (1 + p / 100) * 100) / 100 } : l;
                        }))}
                        className="mx-1 px-2 py-0.5 rounded bg-slate-800 border border-slate-700 hover:bg-slate-700"
                        data-testid={`markup-quick-${p}`}
                      >{p}%</button>
                    ))}
                  </div>
                  <Button onClick={saveMarkup} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-markup-btn">
                    Save Selling Prices
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* ============ Receive Linker Dialog (link/create internal stock before receive) ============ */}
        <Dialog open={!!linkerPO} onOpenChange={(o) => { if (!o) { setLinkerPO(null); setLinkerLines([]); } }}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="receive-linker-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-emerald-400" /> Link Stock Items · {linkerPO?.po_number}
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-400 -mt-2">
              Every stock line must be linked to an <b>internal stock code</b> before receive. Supplier names/SKUs are reference only — quantity &amp; COGS update against your internal stock.
            </p>
            {linkerLoading ? (
              <div className="text-center py-8 text-slate-400">Loading...</div>
            ) : linkerLines.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No stock lines on this PO.</div>
            ) : (
              <div className="space-y-3">
                {linkerLines.map((line, i) => {
                  const linked = !!line.linked_to;
                  const isExpansion = line.item_type === 'expansion';
                  return (
                    <div key={line.line_index} className={`rounded-lg border p-3 space-y-2 ${linked ? 'bg-emerald-950/30 border-emerald-700' : isExpansion ? 'bg-purple-950/30 border-purple-700' : 'bg-amber-950/20 border-amber-700'}`} data-testid={`linker-line-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Badge className={linked ? 'bg-emerald-600 text-white text-[9px]' : isExpansion ? 'bg-purple-600 text-white text-[9px]' : 'bg-amber-600 text-white text-[9px]'}>
                              {linked ? '✓ LINKED' : isExpansion ? '◇ NON-STOCK' : 'UNLINKED'}
                            </Badge>
                            <span className="text-[10px] text-slate-400">qty {line.quantity} · {R(line.unit_price)} ex-VAT each</span>
                          </div>
                          <div className="text-sm text-white mt-1">
                            <span className="text-slate-500 text-xs">Supplier:</span> {line.name}
                            {line.sku && <span className="text-slate-500 text-[10px] font-mono ml-2">({line.sku})</span>}
                          </div>
                          {linked && (
                            <div className="text-xs text-emerald-300 mt-1">
                              → Internal stock: <b>{line.linked_to.name}</b>
                              <span className="text-slate-500 ml-1">(qty on hand: {line.linked_to.quantity})</span>
                            </div>
                          )}
                          {isExpansion && (
                            <div className="text-xs text-purple-300 mt-1">
                              → Non-stock expense under: <b>{line.expansion_project_name || '(project)'}</b>
                            </div>
                          )}
                        </div>
                      </div>

                      {!linked && !isExpansion && (
                        <>
                          <div className="grid grid-cols-3 gap-2 border-t border-slate-700 pt-2">
                            <button
                              type="button"
                              onClick={() => updateLinkerLine(i, { mode: 'pick' })}
                              className={`px-2 py-1.5 rounded text-xs font-semibold ${line.mode === 'pick' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                              data-testid={`linker-mode-pick-${i}`}
                            >
                              Link to existing
                            </button>
                            <button
                              type="button"
                              onClick={() => updateLinkerLine(i, { mode: 'create' })}
                              className={`px-2 py-1.5 rounded text-xs font-semibold ${line.mode === 'create' ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                              data-testid={`linker-mode-create-${i}`}
                            >
                              + Create new
                            </button>
                            <button
                              type="button"
                              onClick={() => updateLinkerLine(i, { mode: 'expansion' })}
                              className={`px-2 py-1.5 rounded text-xs font-semibold ${line.mode === 'expansion' ? 'bg-purple-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                              data-testid={`linker-mode-expansion-${i}`}
                              title="This isn't a stock item — log it as a non-stock (expansion) cost"
                            >
                              ◇ Non-stock
                            </button>
                          </div>

                          {line.mode === 'pick' && (
                            <div className="space-y-1.5">
                              <Input
                                value={line.searching}
                                onChange={(e) => updateLinkerLine(i, { searching: e.target.value })}
                                placeholder="Search internal stock by name / sku / code..."
                                className="bg-slate-800 border-slate-700 h-8 text-sm"
                                data-testid={`linker-search-${i}`}
                              />
                              {(line.searching || '').trim().length > 0 && (() => {
                                const q = line.searching.toLowerCase();
                                const matches = stockCatalog.filter(p =>
                                  (p.name || '').toLowerCase().includes(q) ||
                                  (p.sku || '').toLowerCase().includes(q) ||
                                  (p.id || '').toLowerCase().includes(q)
                                ).slice(0, 10);
                                if (!matches.length) {
                                  return <div className="text-xs text-slate-400 px-2">No matches. Try "+ Create new" or "◇ Non-stock" instead.</div>;
                                }
                                return (
                                  <div className="max-h-40 overflow-y-auto bg-slate-800 rounded border border-slate-700">
                                    {matches.map(p => (
                                      <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => updateLinkerLine(i, { pick_id: p.id, searching: p.name })}
                                        className={`w-full text-left px-2 py-1.5 hover:bg-slate-700 ${line.pick_id === p.id ? 'bg-blue-900/60' : ''}`}
                                        data-testid={`linker-pick-${i}-${p.id}`}
                                      >
                                        <div className="text-sm text-white truncate">{p.name}</div>
                                        <div className="text-[10px] text-slate-500 font-mono">
                                          {p.sku ? `SKU ${p.sku} · ` : ''}cost {R(p.default_cost ?? p.cost_price)}
                                          {typeof p.on_hand === 'number' ? ` · QTY ${p.on_hand}` : ''}
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          )}

                          {line.mode === 'create' && (
                            <div className="grid grid-cols-2 gap-2">
                              <div className="col-span-2">
                                <Label className="text-[10px] text-slate-400">New internal stock NAME *</Label>
                                <Input
                                  value={line.new_name}
                                  onChange={(e) => updateLinkerLine(i, { new_name: e.target.value })}
                                  className="bg-slate-800 border-slate-700 h-8 text-sm"
                                  data-testid={`linker-new-name-${i}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-slate-400">Category</Label>
                                <Input
                                  value={line.new_category}
                                  onChange={(e) => updateLinkerLine(i, { new_category: e.target.value })}
                                  className="bg-slate-800 border-slate-700 h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-slate-400">COGS (cost ex VAT)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={line.new_cost}
                                  onChange={(e) => updateLinkerLine(i, { new_cost: e.target.value })}
                                  className="bg-slate-800 border-slate-700 h-8 text-sm text-right"
                                />
                              </div>
                              <p className="col-span-2 text-[10px] text-slate-500 italic">
                                Selling price stays blank — set it from the PO row's Markup button after receive.
                              </p>
                            </div>
                          )}

                          {line.mode === 'expansion' && (
                            <div className="space-y-1.5">
                              <Label className="text-[10px] text-slate-400">Expansion Project *</Label>
                              <select
                                value={line.new_expansion_project_id || ''}
                                onChange={(e) => updateLinkerLine(i, { new_expansion_project_id: e.target.value })}
                                className="w-full bg-slate-800 border border-slate-700 rounded h-8 px-2 text-sm text-white"
                                data-testid={`linker-expansion-project-${i}`}
                              >
                                <option value="">Pick a project...</option>
                                {linkerProjects.map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </select>
                              {linkerProjects.length === 0 && (
                                <p className="text-[10px] text-amber-400 italic">No active expansion projects — create one in the Expansion Budget tab first.</p>
                              )}
                              <p className="text-[10px] text-slate-500 italic">This line will be logged as a non-stock expense against the project. No inventory will be touched.</p>
                            </div>
                          )}

                          <Button
                            type="button"
                            onClick={() => saveLinkerLine(i)}
                            className={`w-full h-8 ${line.mode === 'expansion' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                            data-testid={`linker-save-${i}`}
                          >
                            {line.mode === 'pick' ? 'Link this line' : line.mode === 'create' ? 'Create + Link this line' : 'Mark as Non-Stock'}
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}

                <div className="flex items-center justify-between border-t border-slate-700 pt-3 mt-3">
                  <div className="text-xs">
                    {linkerLines.filter(l => l.item_type === 'stock' && l.linked_to).length + linkerLines.filter(l => l.item_type === 'expansion').length} of {linkerLines.length} line(s) resolved.
                  </div>
                  <Button
                    onClick={confirmReceive}
                    disabled={linkerLines.some(l => l.item_type === 'stock' && !l.linked_to)}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="confirm-receive-btn"
                  >
                    Confirm Receive
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
    </div>
  );
}

/**
 * Standalone page wrapper — used by the /procurement route in the sidebar.
 */
export default function ProcurementPage() {
  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        <ProcurementContent />
      </div>
    </DashboardLayout>
  );
}
